import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot } from 'firebase/firestore'
import { getAuthOrThrow, getDbOrThrow, googleProvider } from '@/lib/firebase'
import { mapUserProfile } from '@/lib/mappers'
import type { UserProfile } from '@/lib/types'
import { AuthContext, type AuthState } from '@/features/auth/auth-context'

/**
 * Single source of truth for "who is signed in and may they be here".
 *
 * The role is NOT trusted from the client: it is read from `users/{uid}`, and every write the
 * console performs is checked again by the security rules. Hiding a page is convenience, not
 * security.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthLoading, setAuthLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isProfileLoading, setProfileLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return onAuthStateChanged(getAuthOrThrow(), (nextUser) => {
      setUser(nextUser)
      setAuthLoading(false)
      setError(null)
      if (!nextUser) setProfile(null)
    })
  }, [])

  // Live profile: a role change made in another tab (or in the Firebase console) lands here without
  // a reload, which also means a demoted admin loses the UI immediately.
  useEffect(() => {
    if (!user) {
      setProfileLoading(false)
      return
    }
    setProfileLoading(true)
    const ref = doc(getDbOrThrow(), 'users', user.uid)
    return onSnapshot(
      ref,
      (snapshot) => {
        setProfileLoading(false)
        if (!snapshot.exists()) {
          setProfile(null)
          return
        }
        const data = snapshot.data()
        const mapped = mapUserProfile(snapshot.id, data)
        setProfile({
          ...mapped,
          // Fall back to the Auth session when the profile document has no copy of these.
          email: mapped.email ?? user.email,
          displayName: mapped.displayName ?? user.displayName,
        })
      },
      (snapshotError) => {
        setProfileLoading(false)
        setProfile(null)
        setError(snapshotError.message)
      },
    )
  }, [user])

  const signInWithGoogle = useCallback(async () => {
    setError(null)
    try {
      await signInWithPopup(getAuthOrThrow(), googleProvider())
    } catch (caught) {
      const code = (caught as { code?: string }).code ?? ''
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
      setError(describeAuthError(code, caught))
      throw caught
    }
  }, [])

  const signOut = useCallback(async () => {
    await firebaseSignOut(getAuthOrThrow())
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      isLoading: isAuthLoading || isProfileLoading,
      user,
      profile,
      error,
      isAdmin: profile?.role === 'admin',
      signInWithGoogle,
      signOut,
    }),
    [isAuthLoading, isProfileLoading, user, profile, error, signInWithGoogle, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

function describeAuthError(code: string, caught: unknown): string {
  switch (code) {
    case 'auth/popup-blocked':
      return 'Браузер заблокировал всплывающее окно входа. Разрешите popup для этого сайта.'
    case 'auth/unauthorized-domain':
      return 'Домен не добавлен в Firebase Auth → Settings → Authorized domains.'
    case 'auth/operation-not-allowed':
      return 'Провайдер Google не включён в Firebase Authentication → Sign-in method.'
    case 'auth/network-request-failed':
      return 'Нет связи с Firebase. Проверьте интернет.'
    default:
      return (caught as { message?: string }).message ?? 'Не удалось войти.'
  }
}
